// Imperative engine behind the landing page ("The Climb"): fits the Wasdale
// hero art to the viewport, applies scroll parallax, and lays out the gold
// path that runs from the hero summit to the footer.
//
// Kept out of React on purpose. Everything here runs per scroll frame, so it
// writes transforms and attributes straight to the few elements that move
// rather than re-rendering. Three things made the prototype slow and are
// avoided here: CSS/SVG filters on large elements, one page-tall SVG path
// (every dash change repainted the whole page), and per-frame CSS variable
// writes on :root (restyles the whole document).

import { createMarkerImage, PEAK_GLYPH, PEAK_MARKER_PX } from "../../components/map/AtlasMap";
import { MAP_COLORS } from "../../components/map/map-colors";
import {
  HERO_GABLE_ROUTE,
  HERO_IMAGE_HEIGHT as IH,
  HERO_IMAGE_WIDTH as IW,
  HERO_MAIN_ROUTE,
  HERO_PINS,
  HERO_SHORE_ROUTE,
  type HeroPoint,
} from "./heroData";

const SVG_NS = "http://www.w3.org/2000/svg";

/** Scroll parallax per hero layer, far to near: px of lag per px scrolled. */
export const HERO_LAYER_PARALLAX = [0.2, 0.15, 0.1, 0.05, 0] as const;

/** Seconds before the scroll-driven path takes over from the hero line draw. */
const HERO_DRAW_SECONDS = 4;

/** Height of the valley floor, where the path leaves the hero. */
const START_ALTITUDE = 80;
const START_NAME = "Wasdale Head";

// Pins are the app's own peak markers, drawn by the same function the map
// uses, so the landing page can never drift from the real thing.
const markerCache = new Map<string, string>();
// Hero pills that start open stay open only until they have been hovered or
// focused once; after that they behave like the rest. Survives relayouts.
const labelSeen = new Set<string>();
function markerUrl(collected: boolean): string {
  const color = collected ? MAP_COLORS.gold : MAP_COLORS.grey;
  let url = markerCache.get(color);
  if (!url) {
    // Four times the map's bitmap: the page shows these larger than the map
    // does, and at the map's size they were being stretched and went soft.
    const img = createMarkerImage(color, PEAK_MARKER_PX, PEAK_GLYPH, { scale: 4 });
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d")!.putImageData(img, 0, 0);
    url = canvas.toDataURL();
    markerCache.set(color, url);
  }
  return url;
}

type P2 = [number, number];
/** x, y, parallax factor */
type P3 = [number, number, number];

interface Knot {
  x: number;
  y: number;
  pin?: number;
  side?: number;
}

interface Chunk {
  start: number;
  len: number;
  glow: SVGPathElement;
  line: SVGPathElement;
  state: number;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

function mulberry32(a: number) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Resample to even spacing, then round the corners (Chaikin). */
function smooth(raw: P3[], step: number): P3[] {
  if (raw.length < 2) return raw.slice();
  let rs: P3[] = [raw[0]!];
  let acc = 0;
  for (let i = 1; i < raw.length; i++) {
    const a = raw[i - 1]!, b = raw[i]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    let t = step - acc;
    while (t <= len) {
      const f = t / len;
      rs.push([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]);
      t += step;
    }
    acc = (acc + len) % step;
  }
  rs.push(raw[raw.length - 1]!);
  for (let it = 0; it < 3; it++) {
    const o: P3[] = [rs[0]!];
    for (let j = 0; j < rs.length - 1; j++) {
      const p = rs[j]!, q = rs[j + 1]!;
      o.push(
        [p[0] * 0.75 + q[0] * 0.25, p[1] * 0.75 + q[1] * 0.25, p[2] * 0.75 + q[2] * 0.25],
        [p[0] * 0.25 + q[0] * 0.75, p[1] * 0.25 + q[1] * 0.75, p[2] * 0.25 + q[2] * 0.75],
      );
    }
    o.push(rs[rs.length - 1]!);
    rs = o;
  }
  return rs;
}

/** Centripetal Catmull-Rom between p1 and p2; no cusps or loops on uneven spacing. */
function catmullRom(p0: P2, p1: P2, p2: P2, p3: P2, n: number, out: P2[]) {
  const tj = (ti: number, a: P2, b: P2) => ti + Math.max(1e-3, Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1])));
  const t0 = 0, t1 = tj(t0, p0, p1), t2 = tj(t1, p1, p2), t3 = tj(t2, p2, p3);
  for (let i = 0; i < n; i++) {
    const t = t1 + ((t2 - t1) * i) / n;
    const o: P2 = [0, 0];
    for (let c = 0 as 0 | 1; c < 2; c++) {
      const a1 = ((t1 - t) / (t1 - t0)) * p0[c]! + ((t - t0) / (t1 - t0)) * p1[c]!;
      const a2 = ((t2 - t) / (t2 - t1)) * p1[c]! + ((t - t1) / (t2 - t1)) * p2[c]!;
      const a3 = ((t3 - t) / (t3 - t2)) * p2[c]! + ((t - t2) / (t3 - t2)) * p3[c]!;
      const b1 = ((t2 - t) / (t2 - t0)) * a1 + ((t - t0) / (t2 - t0)) * a2;
      const b2 = ((t3 - t) / (t3 - t1)) * a2 + ((t - t1) / (t3 - t1)) * a3;
      o[c] = ((t2 - t) / (t2 - t1)) * b1 + ((t - t1) / (t2 - t1)) * b2;
    }
    out.push(o);
  }
}

function must<T extends Element>(root: ParentNode, sel: string): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`landing: missing ${sel}`);
  return el;
}

export function mountClimb(root: HTMLElement): () => void {
  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const hero = must<HTMLElement>(root, '[data-cl="hero"]');
  const layers = Array.from(hero.querySelectorAll<HTMLElement>(".cl-lay"));
  const overlay = must<SVGSVGElement>(hero, '[data-cl="hov"]');
  const heroMain = must<SVGPathElement>(hero, '[data-cl="hmain"]');
  const heroGable = must<SVGPathElement>(hero, '[data-cl="hgab"]');
  const heroPins = must<SVGGElement>(hero, '[data-cl="hpins"]');
  const journey = must<HTMLElement>(root, '[data-cl="journey"]');
  const routes = must<HTMLElement>(root, '[data-cl="routes"]');
  const marker = must<HTMLElement>(root, '[data-cl="marker"]');
  const hud = must<HTMLElement>(root, '[data-cl="hud"]');
  const hudName = must<HTMLElement>(root, '[data-cl="hud-name"]');
  const hudAlt = must<HTMLElement>(root, '[data-cl="hud-alt"]');
  const dawn = must<HTMLElement>(root, '[data-cl="dawn"]');
  const flag = must<SVGSVGElement>(root, '[data-cl="flag"]');
  const stops = Array.from(journey.querySelectorAll<HTMLElement>("[data-peak]"));

  // ── Hero ───────────────────────────────────────────────────────────────────
  let heroHeight = 0;
  let mainPts: P3[] = [];
  let gablePts: P3[] = [];
  let pinPts: P3[] = [];
  let exit = { x: 0, y: 0, dx: 0, dy: 1 };
  let lastHeroScroll = -1;

  function fitHero() {
    const bw = hero.clientWidth;
    const hh = hero.offsetHeight;
    const mobile = bw <= 760;
    const bh = mobile ? Math.round(hh * 0.52) : hh;
    let s: number, vw: number, vh: number, vx: number, vy: number;
    const gable = HERO_PINS.find((p) => p.name === "Great Gable")!.at;
    const scafell = HERO_PINS.find((p) => p.name === "Scafell Pike")!.at;
    if (mobile) {
      s = bh / IH; vw = bw / s; vh = IH; vy = 0; vx = 470 - vw * 0.12;
    } else {
      s = Math.max(bw / IW, bh / IH); vw = bw / s; vh = bh / s;
      // Skyline a little below centre, so the headline has clear sky.
      vy = Math.min(IH - vh * 0.86, gable[1] - vh * 0.56);
      vx = Math.min(IW - vw, (IW - vw) / 2 + 90);
      if (scafell[0] > vx + vw - 170) vx = Math.min(IW - vw, scafell[0] + 170 - vw);
    }
    const top = hh - bh;
    heroHeight = hh;
    for (const l of layers) {
      const im = l.firstElementChild as HTMLElement | null;
      if (!im) continue;
      im.style.width = `${(IW * s).toFixed(1)}px`;
      im.style.height = `${(IH * s).toFixed(1)}px`;
      im.style.left = `${(-vx * s).toFixed(1)}px`;
      im.style.top = `${(top - vy * s).toFixed(1)}px`;
    }
    overlay.setAttribute("viewBox", `0 0 ${bw} ${hh}`);
    const toScreen = (p: HeroPoint): P3 => [(p[0] - vx) * s, top + (p[1] - vy) * s, HERO_LAYER_PARALLAX[p[2]] ?? 0];

    // Cut the route where it crosses the bottom edge. The shore points sit on
    // the nearest layer (no parallax), so that crossing never moves and the
    // page path can start from exactly the same point.
    const raw = [...HERO_MAIN_ROUTE, ...HERO_SHORE_ROUTE].map(toScreen);
    const cut: P3[] = [];
    for (let i = 0; i < raw.length; i++) {
      const cur = raw[i]!, prev = raw[i - 1];
      if (prev && cur[1] >= hh && prev[1] < hh && i > raw.length / 2) {
        const t = (hh - prev[1]) / (cur[1] - prev[1]);
        cut.push([prev[0] + (cur[0] - prev[0]) * t, hh, 0]);
        break;
      }
      cut.push(cur);
    }
    mainPts = smooth(cut, 30);
    const n = mainPts.length;
    const a = mainPts[Math.max(0, n - 4)]!, b = mainPts[n - 1]!;
    const dl = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    exit = { x: b[0], y: b[1], dx: (b[0] - a[0]) / dl, dy: (b[1] - a[1]) / dl };
    gablePts = smooth(HERO_GABLE_ROUTE.map(toScreen), 30);

    const k = mobile ? 0.62 : clamp01((bw - 900) / 900) * 0.35 + 0.72;
    let html = "";
    pinPts = HERO_PINS.map((p) => {
      const c = toScreen(p.at);
      const w = (p.name.length + 7 + (p.collected ? 0 : 8)) * 11.6 + 34;
      // Keep the pill on screen for pins near an edge.
      const half = (w * k) / 2;
      const dx = (Math.max(12 - (c[0] - half), 0) - Math.max(c[0] + half - (bw - 12), 0)) / k;
      const fixed = p.labelled && !labelSeen.has(p.name) && c[0] > 120 && c[0] < bw - 120;
      // Pins under the headline show their pill below, clear of the type.
      const below = !fixed && !mobile && c[0] < bw * 0.55 && c[1] < hh * 0.56;
      const ry = below ? 38 : -78;
      const state = p.collected ? "collected" : "not yet collected";
      html +=
        `<g class="cl-hpin-wrap" data-pin="${p.name}" tabindex="0" role="img" aria-label="${p.name}, ${p.height} metres, ${state}">` +
        `<g transform="scale(${k.toFixed(2)})">` +
        `<g class="cl-hpin" style="--d:${p.delay}s"><image href="${markerUrl(p.collected)}" x="-23" y="-23" width="46" height="46"/></g>` +
        `<g class="cl-hlabel${fixed ? " is-fixed" : ""}" style="--d:${p.delay}s"><rect x="${dx - w / 2}" y="${ry}" width="${w}" height="40" rx="20"/>` +
        `<text x="${dx}" y="${ry + 27}" text-anchor="middle">${p.name} <tspan>${p.height} m</tspan>` +
        (p.collected ? "" : `<tspan class="is-quiet"> not yet</tspan>`) +
        `</text></g></g></g>`;
      return c;
    });
    heroPins.innerHTML = html;
    for (const wrap of heroPins.querySelectorAll<SVGGElement>(".cl-hpin-wrap")) {
      const label = wrap.querySelector(".cl-hlabel.is-fixed");
      if (!label) continue;
      const release = () => {
        labelSeen.add(wrap.dataset["pin"] ?? "");
        label.classList.remove("is-fixed");
      };
      wrap.addEventListener("pointerleave", release);
      wrap.addEventListener("blur", release);
    }
    moveHero(Math.max(0, lastHeroScroll), true);
  }

  function pathData(pts: P3[], sy: number) {
    let d = "";
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      d += `${i ? "L" : "M"}${p[0].toFixed(1)} ${(p[1] + sy * p[2]).toFixed(1)}`;
    }
    return d;
  }

  function moveHero(sy: number, force = false) {
    if (still) sy = 0;
    if (!force && Math.abs(sy - lastHeroScroll) < 0.5) return;
    lastHeroScroll = sy;
    for (const l of layers) l.style.transform = `translate3d(0,${(sy * Number(l.dataset["f"] ?? 0)).toFixed(1)}px,0)`;
    heroMain.setAttribute("d", pathData(mainPts, sy));
    heroGable.setAttribute("d", pathData(gablePts, sy));
    const g = heroPins.children;
    for (let i = 0; i < g.length; i++) {
      const c = pinPts[i];
      if (c) g[i]!.setAttribute("transform", `translate(${c[0].toFixed(1)} ${(c[1] - 3 + sy * c[2]).toFixed(1)})`);
    }
  }

  // ── Page path ──────────────────────────────────────────────────────────────
  const pins: HTMLElement[] = [];
  stops.forEach((s, i) => {
    if (i === stops.length - 1) return;
    const p = document.createElement("button");
    p.type = "button";
    p.className = "cl-pin";
    p.setAttribute("aria-label", `${s.dataset["peak"] ?? ""}, ${s.dataset["alt"] ?? ""} metres`);
    p.innerHTML =
      `<img src="${markerUrl(false)}" alt=""><img src="${markerUrl(true)}" alt="">` +
      `<span class="cl-pin-tag">${s.dataset["peak"] ?? ""} <b>${s.dataset["alt"] ?? ""} m</b> <i>not yet</i></span>`;
    journey.appendChild(p);
    pins.push(p);
  });

  let samples: P2[] = [];
  let cumLen: number[] = [];
  let anchorScroll: number[] = [];
  let anchorLen: number[] = [];
  let pinLen: number[] = [];
  let chunks: Chunk[] = [];
  let total = 0;

  function layout() {
    fitHero();
    const jr = journey.getBoundingClientRect();
    const W = jr.width;
    const mobile = W <= 760;
    const vh = window.innerHeight;
    const rnd = mulberry32(21);
    const jTop = jr.top + window.scrollY;

    // Knots: where the path must pass. It leaves the hero in the direction it
    // was already travelling, then for each section runs either down the gap
    // between the two columns or down a side margin, with a gentle meander.
    const knots: Knot[] = [{ x: exit.x, y: exit.y }, { x: exit.x + exit.dx * 70, y: exit.y + Math.max(40, exit.dy * 70) }];
    stops.forEach((s, i) => {
      const r = s.getBoundingClientRect();
      const top = r.top - jr.top, bottom = r.bottom - jr.top;
      if (i === stops.length - 1) {
        const f = flag.getBoundingClientRect();
        knots.push({ x: W * (0.5 + (rnd() - 0.5) * 0.2), y: top + 20 });
        knots.push({ x: W * 0.5, y: f.bottom - jr.top, pin: i });
        return;
      }
      const body = s.querySelector<HTMLElement>("[data-body]") ?? s;
      const head = s.querySelector<HTMLElement>(".cl-head") ?? s;
      let cx: number, amp: number, py: number, side = 0;
      const cols = s.querySelector<HTMLElement>(".cl-split")?.children;
      if (mobile) {
        side = i % 2 ? 1 : -1; cx = side > 0 ? W - 24 : 24; amp = 7;
        py = head.getBoundingClientRect().top + 10 - jr.top;
      } else if (s.dataset["route"] === "gap" && cols && cols.length >= 2) {
        const r0 = cols[0]!.getBoundingClientRect(), r1 = cols[1]!.getBoundingClientRect();
        cx = (r0.right + r1.left) / 2; amp = Math.min(46, (r1.left - r0.right) * 0.24);
        py = r0.top - jr.top + 34;
      } else {
        side = s.dataset["run"] === "left" ? -1 : 1;
        const m = Math.max(44, body.getBoundingClientRect().left / 2);
        cx = side < 0 ? m : W - m; amp = Math.min(50, m * 0.42);
        py = head.getBoundingClientRect().top - jr.top + 30;
      }
      knots.push({ x: cx + (rnd() - 0.5) * amp, y: py, pin: i, side });
      let y = py + 200 + rnd() * 80;
      const end = bottom - (mobile ? 30 : 60);
      let sign = rnd() < 0.5 ? -1 : 1;
      while (y < end) {
        knots.push({ x: cx + sign * amp * (0.45 + rnd() * 0.55), y });
        sign = -sign;
        y += 210 + rnd() * 170;
      }
    });
    for (let i = 1; i < knots.length; i++) {
      if (knots[i]!.y <= knots[i - 1]!.y + 20) knots[i]!.y = knots[i - 1]!.y + 20;
    }

    samples = [];
    const knotIndex: number[] = [];
    const lead: P2 = [exit.x - exit.dx * 80, exit.y - exit.dy * 80];
    for (let i = 0; i < knots.length - 1; i++) {
      const k0 = knots[i - 1], k1 = knots[i]!, k2 = knots[i + 1]!, k3 = knots[i + 2];
      const p1: P2 = [k1.x, k1.y], p2: P2 = [k2.x, k2.y];
      const p0: P2 = k0 ? [k0.x, k0.y] : lead;
      const p3: P2 = k3 ? [k3.x, k3.y] : [p2[0], p2[1] + 80];
      knotIndex.push(samples.length);
      catmullRom(p0, p1, p2, p3, Math.max(6, Math.ceil(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / 9)), samples);
    }
    const lastKnot = knots[knots.length - 1]!;
    knotIndex.push(samples.length);
    samples.push([lastKnot.x, lastKnot.y]);

    cumLen = [0];
    for (let i = 1; i < samples.length; i++) {
      const a = samples[i - 1]!, b = samples[i]!;
      cumLen.push(cumLen[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]));
    }
    total = cumLen[cumLen.length - 1] ?? 0;

    // The marker's distance along the path is tied to scroll at every knot and
    // interpolated between them, so it travels along the line (sideways as
    // well as down) instead of tracking the viewport's y position.
    anchorScroll = []; anchorLen = []; pinLen = [];
    knots.forEach((k, i) => {
      const len = cumLen[knotIndex[i]!] ?? 0;
      anchorScroll.push(jTop + k.y - vh * 0.55);
      anchorLen.push(len);
      if (k.pin == null) return;
      pinLen[k.pin] = len;
      const p = pins[k.pin];
      if (!p) return;
      p.style.left = `${k.x}px`;
      p.style.top = `${k.y}px`;
      p.classList.toggle("is-left", (k.side ?? 0) < 0);
      p.classList.toggle("is-right", (k.side ?? 0) > 0);
    });

    // Split the line into short SVGs. Only the piece being drawn changes on
    // scroll, so the browser never repaints a page-tall path.
    routes.textContent = "";
    chunks = [];
    let a = 0;
    while (a < samples.length - 1) {
      let b = a;
      while (b < samples.length - 1 && cumLen[b]! - cumLen[a]! < 760) b++;
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (let i = a; i <= b; i++) {
        const q = samples[i]!;
        x0 = Math.min(x0, q[0]); x1 = Math.max(x1, q[0]); y0 = Math.min(y0, q[1]); y1 = Math.max(y1, q[1]);
      }
      x0 -= 12; y0 -= 12; x1 += 12; y1 += 12;
      let d = "";
      for (let i = a; i <= b; i++) d += `${i > a ? "L" : "M"}${(samples[i]![0] - x0).toFixed(1)} ${(samples[i]![1] - y0).toFixed(1)}`;
      const len = cumLen[b]! - cumLen[a]!;
      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("class", "cl-route");
      svg.setAttribute("width", String(x1 - x0));
      svg.setAttribute("height", String(y1 - y0));
      svg.style.left = `${x0}px`;
      svg.style.top = `${y0}px`;
      const dash = `stroke-dasharray="${len.toFixed(1)}" stroke-dashoffset="${len.toFixed(1)}"`;
      svg.innerHTML = `<path class="cl-route-bg" d="${d}"/><path class="cl-route-glow" d="${d}" ${dash}/><path class="cl-route-line" d="${d}" ${dash}/>`;
      routes.appendChild(svg);
      chunks.push({ start: cumLen[a]!, len, glow: svg.children[1] as SVGPathElement, line: svg.children[2] as SVGPathElement, state: -1 });
      a = b;
    }
    lastLen = -1;
    update();
  }

  const altitudes = [START_ALTITUDE, ...stops.map((s) => Number(s.dataset["alt"] ?? 0))];
  const names = [START_NAME, ...stops.map((s) => s.dataset["peak"] ?? "")];
  let frame = 0;
  let lastLen = -1;
  let lastIndex = -1;
  let lastAlt = "";
  let lastDawn = -1;
  let hudShown = false;
  let heroDrawn = still;

  function update() {
    frame = 0;
    const sy = window.scrollY;
    const vh = window.innerHeight;
    if (sy < heroHeight + 200) moveHero(sy);
    const day = Math.round(clamp01((sy - vh * 0.5) / (document.documentElement.scrollHeight - vh * 1.5)) * 50) / 50;
    if (day !== lastDawn) { lastDawn = day; dawn.style.opacity = String(day); }
    if (!heroDrawn || !anchorScroll.length) return;

    let len = 0;
    const n = anchorScroll.length;
    if (sy >= anchorScroll[n - 1]!) len = total;
    else if (sy > anchorScroll[0]!) {
      let lo = 0, hi = n - 1;
      while (hi - lo > 1) { const m = (lo + hi) >> 1; if (anchorScroll[m]! <= sy) lo = m; else hi = m; }
      len = anchorLen[lo]! + ((anchorLen[hi]! - anchorLen[lo]!) * (sy - anchorScroll[lo]!)) / (anchorScroll[hi]! - anchorScroll[lo]!);
    }
    if (Math.abs(len - lastLen) < 0.4) return;
    lastLen = len;

    for (const c of chunks) {
      const o = len >= c.start + c.len ? 0 : len <= c.start ? c.len : c.len - (len - c.start);
      if (o === c.state) continue;
      c.state = o;
      c.line.setAttribute("stroke-dashoffset", o.toFixed(1));
      c.glow.setAttribute("stroke-dashoffset", o.toFixed(1));
    }
    let lo = 0, hi = cumLen.length - 1;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (cumLen[m]! <= len) lo = m; else hi = m; }
    const a = samples[lo], b = samples[hi];
    if (a && b) {
      const t = cumLen[hi]! > cumLen[lo]! ? (len - cumLen[lo]!) / (cumLen[hi]! - cumLen[lo]!) : 0;
      marker.style.transform = `translate3d(${(a[0] + (b[0] - a[0]) * t).toFixed(1)}px,${(a[1] + (b[1] - a[1]) * t).toFixed(1)}px,0)`;
    }

    let index = 0;
    for (let i = 0; i < pinLen.length; i++) if (len >= (pinLen[i] ?? Infinity) - 2) index = i + 1;
    if (index !== lastIndex) {
      lastIndex = index;
      pins.forEach((p, i) => {
        const on = i < index;
        p.classList.toggle("is-on", on);
        const state = p.querySelector("i");
        if (state) state.textContent = on ? "matched" : "not yet";
      });
      flag.classList.toggle("is-on", index === stops.length);
      hudName.textContent = names[index] ?? "";
    }
    const a0 = altitudes[index] ?? 0, a1 = altitudes[Math.min(index + 1, altitudes.length - 1)] ?? a0;
    const l0 = index ? pinLen[index - 1] ?? 0 : 0, l1 = index < pinLen.length ? pinLen[index] ?? total : total;
    const alt = `${Math.round(a0 + (a1 - a0) * clamp01((len - l0) / Math.max(1, l1 - l0)))} m`;
    if (alt !== lastAlt) { lastAlt = alt; hudAlt.textContent = alt; }
    const show = len > 0 && index < stops.length;
    if (show !== hudShown) { hudShown = show; hud.classList.toggle("is-on", show); }
  }

  const request = () => { if (!frame) frame = requestAnimationFrame(update); };
  let resizeTimer = 0;
  const relayout = (delay: number) => { window.clearTimeout(resizeTimer); resizeTimer = window.setTimeout(layout, delay); };
  const onResize = () => relayout(120);

  window.addEventListener("scroll", request, { passive: true });
  window.addEventListener("resize", onResize);
  window.addEventListener("load", layout);

  // Images and the guides strip change the page height after first paint.
  let lastHeight = 0;
  const ro = "ResizeObserver" in window
    ? new ResizeObserver(() => {
        const h = journey.offsetHeight;
        if (Math.abs(h - lastHeight) > 2) { lastHeight = h; relayout(60); }
      })
    : null;
  ro?.observe(journey);

  const drawTimer = window.setTimeout(() => {
    heroDrawn = true;
    marker.classList.add("is-on");
    lastLen = -1;
    update();
  }, still ? 0 : HERO_DRAW_SECONDS * 1000);

  // Reveal once, and only run the looping panel animations while on screen.
  const reveal = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { e.target.classList.add("is-in"); reveal?.unobserve(e.target); }
      }, { threshold: 0.18 })
    : null;
  const running = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => { for (const e of entries) e.target.classList.toggle("is-running", e.isIntersecting); })
    : null;
  root.querySelectorAll<HTMLElement>("[data-reveal]").forEach((el) => (reveal ? reveal.observe(el) : el.classList.add("is-in")));
  root.querySelectorAll<HTMLElement>("[data-loop]").forEach((el) => (running ? running.observe(el) : el.classList.add("is-running")));

  layout();

  return () => {
    window.removeEventListener("scroll", request);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("load", layout);
    window.clearTimeout(resizeTimer);
    window.clearTimeout(drawTimer);
    if (frame) cancelAnimationFrame(frame);
    ro?.disconnect();
    reveal?.disconnect();
    running?.disconnect();
    pins.forEach((p) => p.remove());
    routes.textContent = "";
  };
}
